import type { RegistrationStatus } from '@ipc/shared'
import type { ReactNode } from 'react'
import { Navigate, useParams } from 'react-router'
import { isApiError } from '~/api/client'
import { useRegistration } from '~/api/hooks'
import { RetryError } from '~/components/member/feedback'
import { FlowStepper } from '~/components/member/FlowStepper'
import { MemberLayout } from '~/components/member/layout'
import { StepSkeleton } from '~/components/member/StepSkeleton'
import { PAGE_RANK, redirectFor, registrationEndedPath } from './flow'
import type { StepKey } from './steps'

/** What a step page needs to render: either the element to return now (`gate`; check `ok`), or the registration and a frame. */
export type StepRegistration =
  | { ok: false; gate: ReactNode; reg?: undefined; providerId?: undefined; frame?: undefined }
  | { ok: true; gate?: undefined; reg: RegistrationStatus; providerId: string; frame: (child: ReactNode) => ReactNode }

/**
 * The gate every registration step page shares: a skeleton while the registration loads, "registration ended" (back to
 * the welcome page, F6) on a 401, an error with "Try again" for anything else, and the redirect when the registration
 * is not at this page. `page: null` skips the redirect (the page decides, e.g. the face step with a still to take).
 */
export function useStepRegistration(
  page: keyof typeof PAGE_RANK | null,
  step: StepKey,
  opts: { backTo?: string; flow?: boolean } = {}
): StepRegistration {
  const { providerId = '' } = useParams()
  const reg = useRegistration()
  const frame = (child: ReactNode) => (
    <MemberLayout flow={opts.flow ?? true} backTo={opts.backTo ?? `/join/${providerId}`}>
      <FlowStepper step={step} />
      {child}
    </MemberLayout>
  )
  if (reg.isPending) return { ok: false, gate: frame(<StepSkeleton />) }
  if (reg.isError) {
    if (isApiError(reg.error) && reg.error.status === 401)
      return { ok: false, gate: <Navigate {...registrationEndedPath(providerId)} replace /> }
    return { ok: false, gate: frame(<RetryError error={reg.error} onRetry={() => void reg.refetch()} />) }
  }
  const to = page ? redirectFor(page, reg.data) : null
  if (to) return { ok: false, gate: <Navigate to={to} replace /> }
  return { ok: true, reg: reg.data, providerId, frame }
}
